import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Artwork, ArtworkRevision } from '@/contracts/family';
import type { ReadingMode } from '@/contracts/content';
import { PLANNED_COLLECTIONS, STORIES, getStory, resolveTemplate } from '@/content/stories';
import { PAPERS, getPaper } from '@/content/papers';
import { LIVING_SCENES } from '@/content/templates';
import { Scene } from '@/content/scenes';
import { TemplateThumb } from '@/features/marketing/TrySample';
import { StoryReader } from '@/features/reader/StoryReader';
import { StoryActivities } from '@/features/activities/StoryActivities';
import { ColoringStudio } from '@/features/studio/ColoringStudio';
import { useStudio } from '@/features/studio/useStudio';
import { LivingScene } from '@/features/editions/LivingScene';
import { ArtworkService, renderRevision } from '@/services/artwork';
import { ProgressService } from '@/services/progress';
import { storyOfTheWeek, collections } from '@/services/editorial';
import { BADGES, BadgeService, EditionService, WORLD_SLOTS, WorldService, type BadgeStats } from '@/services/editions';
import { useSession } from '@/services/session';
import { useWide } from '@/app/useWide';
import { Button } from '@/design-system/Button';
import { Card, Chip, EmptyState, SectionHeading, TornCard } from '@/design-system/Surfaces';
import { SparkleBurst, SparkleField, SparkleGlyph } from '@/design-system/Sparkle';
import { cn } from '@/design-system/cn';
import { IconArrow, IconBack, IconLock } from '@/features/studio/Icons';
import { avatarFor } from './FamilyApp';
import { useChild } from './childContext';



/**
 * Gathers the stats a badge may be based on, and grants any newly earned ones.
 * Deliberately reads only participation and creation — never likes, payment or
 * consecutive days (spec §7 C15).
 */
async function grantBadges(account: string, childId: string): Promise<string[]> {
  const artworks = await ArtworkService.listForChild(account, childId);
  const finished = artworks.filter((a) => a.finishedRevisionId);
  const editions = await EditionService.listForChild(account, childId);
  const placements = await WorldService.listForChild(account, childId);

  const stats: BadgeStats = {
    storiesFinished: await ProgressService.uniqueCompleted(account, childId),
    picturesFinished: finished.length,
    differentTemplatesUsed: new Set(finished.map((a) => a.templateId).filter(Boolean)).size,
    editionsWithArtwork: editions.filter((e) => Object.keys(e.slots).length > 0 || e.coverRevisionId).length,
    worldPlacements: placements.length,
    livingScenesViewed: finished.some((a) => {
      const story = getStory(a.storyId ?? '');
      const tpl = resolveTemplate(a.storyId, a.templateId);
      return !!tpl?.livingSceneId;
    })
      ? 1
      : 0,
  };
  const fresh = await BadgeService.evaluate(account, childId, stats);
  return fresh.map((g) => g.badgeId);
}

/* ------------------------------------------------------------------ *
 * C01 — Home / My World
 * ------------------------------------------------------------------ */

export function ChildHome() {
  const child = useChild();
  const account = useSession((s) => s.accountId);
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(0);
  const [recent, setRecent] = useState<Array<{ artwork: Artwork; url: string | null }>>([]);
  const [continueTo, setContinueTo] = useState<{ label: string; to: string } | null>(null);
  const [earned, setEarned] = useState<string[]>([]);
  const [placedUrls, setPlacedUrls] = useState<Record<string, string>>({});
  const [placements, setPlacements] = useState<Awaited<ReturnType<typeof WorldService.listForChild>>>([]);
  const featured = useMemo(() => storyOfTheWeek(), []);

  useEffect(() => {
    (async () => {
      setCompleted(await ProgressService.uniqueCompleted(account, child.id));
      await grantBadges(account, child.id);
      setEarned(await BadgeService.earnedIds(account, child.id));

      const places = await WorldService.listForChild(account, child.id);
      setPlacements(places);
      const urlMap: Record<string, string> = {};
      for (const pl of places) {
        const rev = await ArtworkService.getRevision(account, pl.artworkRevisionId);
        if (!rev) continue;
        const u = await ArtworkService.previewUrl(account, rev);
        if (u) urlMap[pl.artworkRevisionId] = u;
      }
      setPlacedUrls(urlMap);

      const arts = await ArtworkService.listForChild(account, child.id);
      const withUrls = await Promise.all(
        arts.slice(0, 4).map(async (a) => {
          if (!a.finishedRevisionId) return { artwork: a, url: null };
          const rev = await ArtworkService.getRevision(account, a.finishedRevisionId);
          return { artwork: a, url: rev ? await ArtworkService.previewUrl(account, rev) : null };
        }),
      );
      setRecent(withUrls);

      // "Continue" resolves to the most recent unfinished meaningful thing:
      // an open drawing first, then an unfinished story (spec §7 C01).
      const openDraft = arts.find((a) => !a.finishedRevisionId && a.draftRevisionId);
      if (openDraft) {
        setContinueTo({
          label: `Finish ${openDraft.title}`,
          to: `/app/child/${child.id}/art/${openDraft.id}/edit`,
        });
        return;
      }
      // Resolve across every story: the most recently touched unfinished one
      // first, then anything never started (spec §7 C01).
      const all = await ProgressService.listForChild(account, child.id);
      const unfinished = all
        .filter((p) => !p.firstCompletedAt)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .map((p) => getStory(p.storyId))
        .find(Boolean);
      if (unfinished) {
        setContinueTo({
          label: `Keep reading ${unfinished.title}`,
          to: `/app/child/${child.id}/read/${unfinished.id}`,
        });
        return;
      }
      const unstarted = STORIES.find((st) => !all.some((p) => p.storyId === st.id));
      const next = unstarted ?? STORIES[0];
      setContinueTo({
        label: `${unstarted ? 'Read' : 'Read again'} ${next.title}`,
        to: `/app/child/${child.id}/read/${next.id}`,
      });
    })();
  }, [account, child.id]);

  const a = avatarFor(child.avatar);

  return (
    <div>
      <header className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0" style={{ background: 'var(--gradient-dawn)' }} />
        {/* A quiet sky over the child's own doorway. It is decoration, so it
            is the first thing bedtime removes (spec §8.4). */}
        <SparkleField count={12} seed={31} />
        <div className="relative u-shell flex items-center justify-between py-l">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center gap-s rounded-full border-2 border-line bg-card/80 py-xs pl-xs pr-l"
          >
            <span className={cn('grid h-10 w-10 place-items-center rounded-full text-[22px]', a.tint)}>
              <span aria-hidden>{a.emoji}</span>
            </span>
            <span className="font-display text-[16px] font-semibold text-ink">{child.nickname}</span>
          </button>
          <Button variant="outline" size="sm" to="/app/parent" icon={<IconLock />}>
            <span className="max-[420px]:sr-only">Grown-ups</span>
          </Button>
        </div>

        {/* My World: a decorative home scene that accumulates their creations.
            Navigation is never hidden behind decoration (spec §7 C01). */}
        <div className="relative u-shell pb-xl">
          <div className="overflow-hidden rounded-blob border-[3px] border-line shadow-cut-lg">
            <div className="relative">
              <Scene
                id="world-room"
                fit="contain"
                className="aspect-[3/2] w-full"
                title={`${child.nickname}'s world`}
              />
              {placements.map((pl) => {
                const def = WORLD_SLOTS.find(
                  (sd) => sd.kind === pl.slot && sd.index === pl.slotIndex,
                );
                const u = placedUrls[pl.artworkRevisionId];
                if (!def || !u) return null;
                return (
                  <span
                    key={pl.id}
                    className="absolute block overflow-hidden"
                    style={{ left: `${def.x}%`, top: `${def.y}%`, width: `${def.w}%`, height: `${def.h}%` }}
                  >
                    <img
                      src={u}
                      alt=""
                      className="absolute inset-0 h-auto max-h-full w-auto max-w-full rounded-sm bg-white shadow-lift"
                      style={{ margin: 'auto' }}
                    />
                  </span>
                );
              })}
              {/* Decoration never sits on a navigation target (spec §7 C01). */}
              <Link
                to={`/app/child/${child.id}/world`}
                className="absolute bottom-m left-m inline-flex min-h-touch items-center gap-s rounded-full border-2 border-line bg-card/95 px-l font-display text-[14px] font-semibold text-ink shadow-cut"
              >
                {placements.length === 0 ? 'Decorate my world' : 'Change my world'}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="u-shell space-y-2xl py-xl">
        {continueTo ? (
          <TornCard tint="primary" className="p-xl">
            <p className="font-display text-[14px] font-semibold uppercase tracking-wide text-primary-deep">
              Carry on
            </p>
            <h2 className="mt-xs font-display text-[24px] font-semibold text-ink sm:text-[28px]">
              {continueTo.label}
            </h2>
            <Button variant="magic" size="xl" to={continueTo.to} className="mt-l" iconRight={<IconArrow />}>
              Continue
            </Button>
          </TornCard>
        ) : null}

        {featured ? (
          <section>
            <SectionHeading eyebrow="Story of the week" title={featured.story.title} />
            <button
              onClick={() => navigate(`/app/child/${child.id}/books/${featured.story.id}`)}
              className="mt-l block w-full overflow-hidden rounded-blob border-[3px] border-magic/40 bg-card text-left shadow-cut transition hover:-translate-y-1"
            >
              <Scene id={featured.story.coverSceneId} className="aspect-[3/2] w-full" title={featured.story.title} />
              <div className="p-l">
                <Chip tone="magic" icon={<SparkleGlyph className="h-3 w-3" />}>This week's pick</Chip>
                <p className="mt-s text-[15px] leading-relaxed text-ink-soft">{featured.story.synopsis}</p>
              </div>
            </button>
            {/* A suggestion, never an obligation (spec §8.4). */}
            <p className="mt-s text-[14px] text-ink-faint">
              Just a suggestion. Nothing runs out, and nothing is lost if you read something else.
            </p>
          </section>
        ) : null}

        <section>
          <SectionHeading eyebrow="Your books" title="Ready to read" />
          <div className="mt-l grid gap-l sm:grid-cols-2">
            {STORIES.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/app/child/${child.id}/books/${s.id}`)}
                className="overflow-hidden rounded-blob border-2 border-line bg-card text-left shadow-cut transition hover:-translate-y-1"
              >
                <Scene id={s.coverSceneId} className="aspect-[3/2] w-full" title={s.title} />
                <div className="p-l">
                  <h3 className="font-display text-[19px] font-semibold text-ink">{s.title}</h3>
                  <p className="mt-xs text-[15px] text-ink-soft">About {s.currentVersion.readMinutes} minutes</p>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section>
          <SectionHeading eyebrow="Your collection" title="What you have done" />
          <div className="mt-l grid gap-m sm:grid-cols-3">
            <Stat label="Stories finished" value={completed} note="Counted once each" />
            <Stat label="Pictures made" value={recent.filter((r) => r.url).length} note="Saved on this device" />
            <Stat
              label="Badges"
              value={earned.length}
              note={earned.length === 0 ? 'Finish a story to earn one' : 'Yours to keep'}
            />
          </div>

          {earned.length > 0 ? (
            <div className="mt-l flex flex-wrap gap-s">
              {BADGES.filter((b) => earned.includes(b.id)).map((b) => (
                <span
                  key={b.id}
                  title={b.description}
                  className="inline-flex items-center gap-s rounded-full border-2 border-line bg-card px-l py-s"
                >
                  <span aria-hidden className="text-[18px]">{b.emoji}</span>
                  <span className="font-display text-[14px] font-semibold text-ink">{b.name}</span>
                </span>
              ))}
            </div>
          ) : null}
          <p className="mt-m text-[14px] text-ink-faint">
            No streaks here. Reading on lots of days in a row is lovely, but missing one is not a
            loss.
          </p>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <Card className="text-center">
      <p
        data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className="font-display text-[38px] font-semibold text-magic"
      >
        {value}
      </p>
      <p className="font-display text-[16px] font-semibold text-ink">{label}</p>
      <p className="mt-xs text-[14px] text-ink-faint">{note}</p>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * C02 — Books
 * ------------------------------------------------------------------ */

export function ChildBooks() {
  const child = useChild();
  const navigate = useNavigate();
  const featured = useMemo(() => storyOfTheWeek(), []);
  const groups = useMemo(() => collections(), []);

  return (
    <div className="u-shell py-xl">
      <SectionHeading eyebrow="Books" title="Everything you can read" />

      {/* Grouped by collection — "same story, different imaginations" is a
          browse aid, not a completion requirement (spec §8.4, F17). */}
      {groups.map((group) => (
        <section key={group.name} className="mt-xl">
          <h2 className="font-display text-[20px] font-semibold text-ink">{group.name}</h2>
          <p className="mt-xs text-[14px] text-ink-soft">
            {group.stories.length === 1
              ? '1 book, with more of these friends to come'
              : `${group.stories.length} books with the same friends in them`}
          </p>
          <div className="mt-l grid gap-l sm:grid-cols-2 lg:grid-cols-3">
            {group.stories.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/app/child/${child.id}/books/${s.id}`)}
                className={cn(
                  'overflow-hidden rounded-blob border-2 bg-card text-left shadow-cut transition hover:-translate-y-1',
                  featured?.story.id === s.id ? 'border-magic/50' : 'border-line',
                )}
              >
                <Scene id={s.coverSceneId} className="aspect-[4/3] w-full" title={s.title} />
                <div className="p-l">
                  <div className="flex flex-wrap gap-xs">
                    <Chip tone="leaf">Free</Chip>
                    {featured?.story.id === s.id ? (
                      <Chip tone="magic" icon={<SparkleGlyph className="h-3 w-3" />}>This week</Chip>
                    ) : null}
                  </div>
                  <h3 className="mt-s font-display text-[18px] font-semibold text-ink">{s.title}</h3>
                  <p className="mt-xs text-[14px] text-ink-soft">
                    About {s.currentVersion.readMinutes} min
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}

      <section className="mt-2xl">
        <h2 className="font-display text-[20px] font-semibold text-ink">More collections coming</h2>
        <div className="mt-l grid gap-m sm:grid-cols-3">
          {PLANNED_COLLECTIONS.filter((c) => c.status === 'planned').map((c) => (
            <div key={c.id} className="rounded-blob border-2 border-dashed border-line p-l">
              <h3 className="font-display text-[16px] font-semibold text-ink-soft">{c.title}</h3>
              <p className="mt-xs text-[14px] text-ink-faint">Being written</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * C03 — Story detail
 * ------------------------------------------------------------------ */

export function StoryDetail() {
  const child = useChild();
  const { storyId } = useParams();
  const account = useSession((s) => s.accountId);
  const navigate = useNavigate();
  const story = getStory(storyId ?? '');
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (!story) return;
    void ProgressService.get(account, child.id, story.id).then((p) => setUnlocked(!!p?.artUnlocked));
  }, [account, child.id, story]);

  if (!story) return <Navigate2 />;

  return (
    <div className="u-shell py-xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} icon={<IconBack />}>
        Back
      </Button>

      <div className="mt-l overflow-hidden rounded-blob border-[3px] border-line shadow-cut-lg">
        <Scene id={story.coverSceneId} className="aspect-[3/2] w-full" title={story.title} />
      </div>

      <h1 className="mt-xl font-display text-[30px] font-semibold text-ink">{story.title}</h1>
      <p className="mt-s max-w-2xl text-[17px] leading-relaxed text-ink-soft">{story.synopsis}</p>

      <div className="mt-l flex flex-wrap gap-s">
        {story.themes.map((t) => (
          <Chip key={t} tone="accent">{t}</Chip>
        ))}
      </div>

      <div className="mt-xl flex flex-wrap gap-m">
        <Button
          variant="magic"
          size="xl"
          to={`/app/child/${child.id}/read/${story.id}`}
          iconRight={<IconArrow />}
        >
          {child.readingMode === 'listen-together' ? 'Listen to it' : 'Read it'}
        </Button>
        {unlocked ? (
          <Button variant="outline" size="xl" to={`/app/child/${child.id}/create/${story.id}`}>
            Go and make a picture
          </Button>
        ) : null}
      </div>

      <section className="mt-2xl">
        <h2 className="font-display text-[20px] font-semibold text-ink">Pictures in this story</h2>
        <p className="mt-xs text-[15px] text-ink-soft">
          {unlocked
            ? 'These are yours to make.'
            : 'Read the story and explore it, and these open up.'}
        </p>
        <div className="mt-l grid gap-l sm:grid-cols-3">
          {story.currentVersion.templates.map((t) => (
            <Card key={t.id} className={cn(!unlocked && 'opacity-70')}>
              <div className="aspect-square w-full rounded-pebble bg-white p-m">
                <TemplateThumb templateId={t.id} />
              </div>
              <h3 className="mt-m font-display text-[16px] font-semibold text-ink">{t.title}</h3>
              {t.livingSceneId ? (
                <Chip tone="magic" className="mt-s" icon={<SparkleGlyph className="h-3 w-3" />}>
                  Comes to life
                </Chip>
              ) : null}
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function Navigate2() {
  return (
    <div className="u-shell py-3xl">
      <EmptyState title="That book is not here" body="It may not be published yet." />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * C04 / C05 — Reader and activities
 * ------------------------------------------------------------------ */

export function ChildReader() {
  const child = useChild();
  const { storyId } = useParams();
  const account = useSession((s) => s.accountId);
  const updateChild = useSession((s) => s.updateChild);
  const navigate = useNavigate();
  const story = getStory(storyId ?? '');
  const [pageId, setPageId] = useState<string | null>(null);
  const [mode, setMode] = useState<ReadingMode>(child.readingMode);

  useEffect(() => {
    if (!story) return;
    void ProgressService.ensure(account, child.id, story).then((p) =>
      setPageId(p.currentPageId ?? story.currentVersion.pages[0].id),
    );
  }, [account, child.id, story]);

  const onPageChange = useCallback(
    (id: string) => {
      setPageId(id);
      if (story) void ProgressService.setPage(account, child.id, story, id);
    },
    [account, child.id, story],
  );

  if (!story) {
    return (
      <div className="u-shell py-3xl">
        <EmptyState title="That book is not here" body="It may not be published yet." />
      </div>
    );
  }
  if (!pageId) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-paper" role="status" aria-label="Opening the story">
        <SparkleGlyph className="h-10 w-10 animate-twinkle text-magic" />
      </div>
    );
  }

  return (
    <StoryReader
      story={story}
      pageId={pageId}
      mode={mode}
      onModeChange={(m) => {
        setMode(m);
        void updateChild(child.id, { readingMode: m });
      }}
      onPageChange={onPageChange}
      onFinish={() => navigate(`/app/child/${child.id}/activities/${story.id}`)}
      onExit={() => navigate(`/app/child/${child.id}/home`)}
      bedtime={child.bedtime}
      onBedtimeChange={(on) => void updateChild(child.id, { bedtime: on })}
    />
  );
}

export function ChildActivities() {
  const child = useChild();
  const { storyId } = useParams();
  const account = useSession((s) => s.accountId);
  const navigate = useNavigate();
  const story = getStory(storyId ?? '');
  if (!story) {
    return (
      <div className="u-shell py-3xl">
        <EmptyState title="That book is not here" body="It may not be published yet." />
      </div>
    );
  }

  return (
    <StoryActivities
      story={story}
      onExit={() => navigate(`/app/child/${child.id}/read/${story.id}`)}
      onRevisitPage={(pid) => {
        void ProgressService.setPage(account, child.id, story, pid);
        navigate(`/app/child/${child.id}/read/${story.id}`);
      }}
      onComplete={async (method) => {
        await ProgressService.completeParticipation(account, child.id, story, method);
        navigate(`/app/child/${child.id}/create/${story.id}`);
      }}
      bedtime={child.bedtime}
      onFinishTomorrow={async () => {
        // Participation still counts — stopping is a choice, not a failure.
        await ProgressService.completeParticipation(account, child.id, story, 'answered');
        navigate(`/app/child/${child.id}/home`);
      }}
    />
  );
}

/* ------------------------------------------------------------------ *
 * C06 / C07 — Create
 * ------------------------------------------------------------------ */

export function ChildCreate() {
  const child = useChild();
  const { storyId } = useParams();
  const account = useSession((s) => s.accountId);
  const navigate = useNavigate();
  const story = getStory(storyId ?? STORIES[0].id)!;
  const [unlocked, setUnlocked] = useState<boolean | null>(null);

  useEffect(() => {
    void ProgressService.get(account, child.id, story.id).then((p) => setUnlocked(!!p?.artUnlocked));
  }, [account, child.id, story.id]);

  const open = async (templateId: string) => {
    const tpl = story.currentVersion.templates.find((t) => t.id === templateId)!;
    const art = await ArtworkService.openForTemplate(
      account, child.id, story.id, tpl.id, tpl.revision, tpl.title,
    );
    navigate(`/app/child/${child.id}/art/${art.id}/edit`);
  };

  /**
   * A drawing of the child's own, prompted by the story they just read
   * (spec §0.1 F12). It is offered next to the prepared pictures rather than
   * hidden somewhere else, because "draw your own" is the same size of idea
   * as "colour this one in" — and it needs no unlocking, because there is
   * nothing to unlock.
   */
  const openPaper = async (paperId: string) => {
    const paper = getPaper(paperId)!;
    const art = await ArtworkService.openPaper(
      account, child.id, paper, story.id, `My own picture`,
    );
    navigate(`/app/child/${child.id}/art/${art.id}/edit`);
  };

  if (unlocked === null) return null;

  return (
    <div className="u-shell py-xl">
      <SectionHeading
        eyebrow="Create"
        title="Pick a picture to make"
        description={
          unlocked
            ? 'Ready now — no waiting for anything to be made.'
            : 'Read the story and explore it first, then these open up.'
        }
      />

      {!unlocked ? (
        <div className="mt-xl">
          <EmptyState
            title="Read the story first"
            body="Not because you have to pass anything — just so the pictures mean something when you get to them."
            action={
              <Button variant="magic" size="lg" to={`/app/child/${child.id}/read/${story.id}`}>
                Read {story.title}
              </Button>
            }
          />
        </div>
      ) : (
        <div className="mt-xl grid gap-l sm:grid-cols-3">
          {story.currentVersion.templates.map((tpl, i) => (
            <button
              key={tpl.id}
              onClick={() => open(tpl.id)}
              className={cn(
                'overflow-hidden rounded-blob border-[3px] border-line bg-card text-left shadow-cut transition hover:-translate-y-1.5 animate-pop-in',
                [`u-stack-1`, `u-stack-2`, `u-stack-3`][i],
              )}
            >
              <div className="aspect-square w-full bg-white p-m">
                <TemplateThumb templateId={tpl.id} />
              </div>
              <div className="border-t-2 border-line p-l">
                <h3 className="font-display text-[17px] font-semibold text-ink">{tpl.title}</h3>
                {tpl.livingSceneId ? (
                  <Chip tone="magic" className="mt-s" icon={<SparkleGlyph className="h-3 w-3" />}>
                    Comes to life
                  </Chip>
                ) : (
                  <Chip tone="neutral" className="mt-s">Colour it in</Chip>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-2xl">
        <PaperPicker
          heading="Or draw your own"
          body={`Nothing to colour in — a blank page and every pen. Draw what you think happened after ${story.title.replace(/^Pip and the /, 'the ')}, or anything at all.`}
          onPick={openPaper}
        />
      </div>
    </div>
  );
}

/**
 * The blank-paper chooser.
 *
 * Three shapes rather than one, because a tall picture and a wide picture are
 * different ideas to a child, and choosing the shape first is easier than
 * discovering later that the page is the wrong way round.
 */
export function PaperPicker({
  heading,
  body,
  onPick,
}: {
  heading: string;
  body: string;
  onPick(paperId: string): void;
}) {
  return (
    <div className="rounded-blob border-2 border-dashed border-line bg-card p-l">
      <h3 className="font-display text-[19px] font-semibold text-ink">{heading}</h3>
      <p className="mt-xs max-w-xl text-[15px] text-ink-soft">{body}</p>
      <div className="mt-l flex flex-wrap items-end gap-m">
        {PAPERS.map((paper) => (
          <button
            key={paper.id}
            onClick={() => onPick(paper.id)}
            className="flex flex-col items-center gap-s rounded-pebble border-2 border-line bg-paper p-m transition hover:-translate-y-1 hover:shadow-cut"
          >
            <span
              aria-hidden
              className="block rounded-sm border-2 border-line bg-white shadow-cut"
              style={{
                width: paper.width >= paper.height ? 76 : (76 * paper.width) / paper.height,
                height: paper.height >= paper.width ? 76 : (76 * paper.height) / paper.width,
              }}
            />
            <span className="font-display text-[15px] font-semibold text-ink-soft">{paper.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * C08 — Studio
 * ------------------------------------------------------------------ */

export function ChildStudio() {
  const child = useChild();
  const { artworkId } = useParams();
  const account = useSession((s) => s.accountId);
  const navigate = useNavigate();
  const wide = useWide();
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artworkId) return;
    void ArtworkService.get(account, artworkId).then((a) => setArtwork(a ?? null));
  }, [account, artworkId]);

  const template = useMemo(() => {
    // A blank page belongs to no story, so the story id is not part of the
    // guard — only the template id has to be there.
    if (!artwork?.templateId) return null;
    return resolveTemplate(artwork.storyId, artwork.templateId) ?? null;
  }, [artwork]);

  const { engine, saveState, requestSave, flush, restoreProblem } = useStudio(account, artwork, template);

  const done = async () => {
    if (!engine || !artwork) return;
    setBusy(true);
    setError(null);
    try {
      await flush();
      const preview = await engine.toBlob(1);
      await ArtworkService.finish(account, artwork, engine.snapshot(), preview, `finish-${artwork.id}`);
      // Route to the actual saved-art result, not back to home (spec §19.1).
      navigate(`/app/child/${child.id}/art/${artwork.id}/done`);
    } catch {
      setError('Your picture is still here, but it would not save. Try Done again, or free up space.');
    } finally {
      setBusy(false);
    }
  };

  if (!artwork || !template || !engine) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <SparkleGlyph className="h-10 w-10 animate-twinkle text-magic" />
      </div>
    );
  }

  return (
    <>
      {restoreProblem || error ? (
        <div role="alert" className="bg-secondary-soft px-l py-m text-[15px] text-ink">
          {error ?? restoreProblem}
        </div>
      ) : null}
      <ColoringStudio
        template={template}
        engine={engine}
        saveState={saveState}
        onRequestSave={requestSave}
        onDone={done}
        onBack={() => navigate(`/app/child/${child.id}/create/${artwork.storyId}`)}
        busy={busy}
        wide={wide}
        handedness="right"
        calm={child.bedtime}
      />
    </>
  );
}

/* ------------------------------------------------------------------ *
 * C09 — Saved-art celebration
 * ------------------------------------------------------------------ */

export function ArtDone() {
  const child = useChild();
  const { artworkId } = useParams();
  const account = useSession((s) => s.accountId);
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [livingUrl, setLivingUrl] = useState<string | null>(null);
  const [showLiving, setShowLiving] = useState(false);
  const [editionSlot, setEditionSlot] = useState<{ pageId: string; caption: string; storyId: string } | null>(null);
  const [newBadges, setNewBadges] = useState<string[]>([]);
  const [addedToBook, setAddedToBook] = useState(false);

  useEffect(() => {
    (async () => {
      if (!artworkId) return;
      const a = await ArtworkService.get(account, artworkId);
      if (!a) return;
      setArtwork(a);
      if (!a.finishedRevisionId) return;
      const rev = await ArtworkService.getRevision(account, a.finishedRevisionId);
      if (!rev) return;
      setUrl(await ArtworkService.previewUrl(account, rev));

      const tpl = a.templateId
        ? resolveTemplate(a.storyId, a.templateId)
        : null;
      const sc = tpl?.livingSceneId ? LIVING_SCENES[tpl.livingSceneId] : null;
      if (tpl && sc) {
        // Re-rendered from stored vector state, with the background omitted so
        // the subject sits IN the scene rather than on a coloured rectangle.
        setLivingUrl(await renderRevision(account, rev, tpl, { omitRegions: sc.omitRegions }));
      }

      // Does this picture have an approved home in the child's own edition?
      const story = getStory(a.storyId ?? '');
      if (story) {
        const slot = EditionService.slotForRevision(story, rev);
        setEditionSlot(slot ? { pageId: slot.pageId, caption: slot.caption, storyId: story.id } : null);
      }
      setNewBadges(await grantBadges(account, child.id));
    })();
  }, [account, artworkId, child.id]);

  const template = artwork?.templateId
    ? resolveTemplate(artwork.storyId, artwork.templateId)
    : null;
  const scene = template?.livingSceneId ? LIVING_SCENES[template.livingSceneId] : null;

  if (!artwork || !url) return null;

  return (
    <div className="u-shell py-2xl">
      <div className="relative mb-xl text-center">
        <SparkleBurst />
        <h1 className="relative font-display text-[30px] font-semibold text-ink sm:text-[38px]">
          You made it.
        </h1>
        <p className="mt-s text-[16px] text-ink-soft">Saved on this device. It is yours.</p>
      </div>

      {showLiving && scene ? (
        <LivingScene
          scene={scene}
          artworkUrl={livingUrl ?? url}
          backdropSceneId={scene.backdropSceneId}
          title={artwork.title}
          onClose={() => setShowLiving(false)}
        />
      ) : (
        <div className="overflow-hidden rounded-blob border-2 border-line bg-white p-xl shadow-cut-lg">
          <img src={url} alt={artwork.title} className="mx-auto max-h-[52vh] w-auto" />
        </div>
      )}

      <div className="mt-xl flex flex-wrap justify-center gap-m">
        {scene && !showLiving ? (
          <Button variant="magic" size="lg" onClick={() => setShowLiving(true)} icon={<SparkleGlyph />}>
            Bring my story to life
          </Button>
        ) : null}
        {editionSlot ? (
          <Button
            variant={addedToBook ? 'outline' : 'primary'}
            size="lg"
            loading={false}
            onClick={async () => {
              const story = getStory(editionSlot.storyId);
              const rev = artwork.finishedRevisionId
                ? await ArtworkService.getRevision(account, artwork.finishedRevisionId)
                : null;
              if (!story || !rev) return;
              const ed = await EditionService.ensureForStory(account, child.id, story);
              // Placing is explicit — a picture never enters an edition on its
              // own, and editing later never silently replaces it (spec §10.1).
              await EditionService.placeInSlot(account, ed, story, editionSlot.pageId, rev);
              setAddedToBook(true);
              setNewBadges(await grantBadges(account, child.id));
            }}
          >
            {addedToBook ? 'It is in your book' : 'Put it in my book'}
          </Button>
        ) : null}
        <Button variant="outline" size="lg" to={`/app/child/${child.id}/my-art`}>
          See all my pictures
        </Button>
      </div>

      {addedToBook && editionSlot ? (
        <p className="mt-m text-center text-[16px] text-ink-soft animate-pop-in">
          {editionSlot.caption} — it is on that page in your own copy now.{' '}
          <Link
            to={`/app/child/${child.id}/editions/${editionSlot.storyId}/build`}
            className="font-semibold text-primary underline underline-offset-4"
          >
            See your copy
          </Link>
        </p>
      ) : null}

      {newBadges.length > 0 ? (
        <div className="mt-xl rounded-blob border-2 border-line bg-sunny/15 p-l text-center animate-pop-in">
          <p className="font-display text-[16px] font-semibold text-ink">
            {newBadges.length === 1 ? 'You earned a badge' : `You earned ${newBadges.length} badges`}
          </p>
          <div className="mt-m flex flex-wrap justify-center gap-m">
            {newBadges.map((id) => {
              const def = BADGES.find((b) => b.id === id);
              if (!def) return null;
              return (
                <span key={id} className="inline-flex items-center gap-s rounded-full border-2 border-sunny/50 bg-card px-l py-s">
                  <span aria-hidden className="text-[20px]">{def.emoji}</span>
                  <span className="font-display text-[15px] font-semibold text-ink">{def.name}</span>
                </span>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Sharing is a request to a grown-up, never an upload (spec §7 C09). */}
      <Card className="mt-2xl bg-paper-deep/60 text-center">
        <p className="text-[16px] text-ink-soft">
          Want other people to see it? That has to go through a grown-up — and the gallery is not
          open yet, so for now it stays just yours.
        </p>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * C10 — My Art
 * ------------------------------------------------------------------ */

export function MyArt() {
  const child = useChild();
  const account = useSession((s) => s.accountId);
  const navigate = useNavigate();
  const [items, setItems] = useState<Array<{ artwork: Artwork; url: string | null; rev: ArtworkRevision | null }>>([]);
  const [tab, setTab] = useState<'progress' | 'finished' | 'editions'>('finished');
  const [editions, setEditions] = useState<Array<{ id: string; storyId: string; title: string; count: number }>>([]);

  useEffect(() => {
    (async () => {
      const arts = await ArtworkService.listForChild(account, child.id);
      const mapped = await Promise.all(
        arts.map(async (a) => {
          if (!a.finishedRevisionId) return { artwork: a, url: null, rev: null };
          const rev = await ArtworkService.getRevision(account, a.finishedRevisionId);
          return { artwork: a, url: rev ? await ArtworkService.previewUrl(account, rev) : null, rev: rev ?? null };
        }),
      );
      setItems(mapped);

      const eds = await EditionService.listForChild(account, child.id);
      setEditions(
        eds.map((e) => ({
          id: e.id,
          storyId: e.storyId,
          title: getStory(e.storyId)?.title ?? 'A story',
          count: Object.keys(e.slots).length + (e.coverRevisionId ? 1 : 0),
        })),
      );
    })();
  }, [account, child.id]);

  const inProgress = items.filter((i) => !i.artwork.finishedRevisionId);
  const finished = items.filter((i) => i.artwork.finishedRevisionId);
  const shown = tab === 'finished' ? finished : inProgress;

  /** A drawing started here belongs to no story — it is simply the child's. */
  const startBlank = async (paperId: string) => {
    const paper = getPaper(paperId)!;
    const art = await ArtworkService.openPaper(account, child.id, paper, null, 'My own picture');
    navigate(`/app/child/${child.id}/art/${art.id}/edit`);
  };

  return (
    <div className="u-shell py-xl">
      <SectionHeading eyebrow="My Art" title="Everything you have made" />

      <div className="mt-l">
        <PaperPicker
          heading="Start something new"
          body="A blank page and every pen. It does not have to be about a story."
          onPick={startBlank}
        />
      </div>

      <div className="mt-l flex gap-s" role="tablist">
        {([
          ['finished', `Finished (${finished.length})`],
          ['progress', `In progress (${inProgress.length})`],
          ['editions', `My books (${editions.length})`],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={cn(
              'min-h-touch rounded-pebble border-2 px-l font-display text-[15px] font-semibold',
              tab === id ? 'border-magic bg-magic/10 text-magic-deep' : 'border-line bg-card text-ink-soft',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'editions' ? (
        editions.length === 0 ? (
          <div className="mt-xl">
            <EmptyState
              title="No books of your own yet"
              body="Finish a picture, then put it in your own copy of the story it came from."
              action={
                <Button variant="magic" size="lg" to={`/app/child/${child.id}/books`}>
                  Find a story
                </Button>
              }
            />
          </div>
        ) : (
          <div className="mt-xl grid gap-l sm:grid-cols-2">
            {editions.map((e) => (
              <Card key={e.id}>
                <div className="overflow-hidden rounded-pebble border-2 border-line">
                  <Scene id={getStory(e.storyId)?.coverSceneId ?? ''} className="aspect-[3/2] w-full" />
                </div>
                <h3 className="mt-m font-display text-[18px] font-semibold text-ink">{e.title}</h3>
                <Chip tone={e.count > 0 ? 'magic' : 'neutral'} className="mt-s">
                  {e.count === 0 ? 'Nothing added yet' : `${e.count} of your pictures`}
                </Chip>
                <div className="mt-m flex flex-wrap gap-s">
                  <Button variant="magic" size="sm" to={`/app/child/${child.id}/editions/${e.id}`}>
                    Read my copy
                  </Button>
                  <Button variant="outline" size="sm" to={`/app/child/${child.id}/editions/${e.storyId}/build`}>
                    Change it
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : shown.length === 0 ? (
        <div className="mt-xl">
          <EmptyState
            title={tab === 'finished' ? 'No finished pictures yet' : 'Nothing half-done'}
            body="Read a story, explore it, then pick a picture to colour."
            action={
              <Button variant="magic" size="lg" to={`/app/child/${child.id}/books`}>
                Find a story
              </Button>
            }
          />
        </div>
      ) : (
        <div className="mt-xl grid gap-l sm:grid-cols-2 lg:grid-cols-3">
          {shown.map(({ artwork, url }) => (
            <Card key={artwork.id}>
              <div className="aspect-square w-full overflow-hidden rounded-pebble border-2 border-line bg-white">
                {url ? (
                  <img src={url} alt={artwork.title} className="h-full w-full object-contain" />
                ) : artwork.templateId ? (
                  <div className="h-full w-full p-m opacity-40">
                    <TemplateThumb templateId={artwork.templateId} />
                  </div>
                ) : null}
              </div>
              <h3 className="mt-m font-display text-[17px] font-semibold text-ink">{artwork.title}</h3>
              <Chip tone={artwork.finishedRevisionId ? 'leaf' : 'sunny'} className="mt-s">
                {artwork.finishedRevisionId ? 'Saved' : 'Still going'}
              </Chip>
              <div className="mt-m flex gap-s">
                <Button
                  variant="outline"
                  size="sm"
                  to={
                    artwork.finishedRevisionId
                      ? `/app/child/${child.id}/art/${artwork.id}/done`
                      : `/app/child/${child.id}/art/${artwork.id}/edit`
                  }
                >
                  {artwork.finishedRevisionId ? 'Look at it' : 'Carry on'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
